import { useState, useEffect, useRef } from 'react';
import { X, Link as LinkIcon, Check } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { getToken } from '@/lib/token-storage';
import { Button } from './ui/button';
import { getTranslations } from '@/lib/translations';
import { useApp } from '@/lib/context';
import { useAuth } from '@/hooks/use-auth';

interface PartnerConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  userLanguage?: string;
}

export default function PartnerConfirmationModal({
  isOpen,
  onClose,
  userLanguage,
}: PartnerConfirmationModalProps) {
  const { user } = useApp();
  const { guestLogin, confirmPartnerInvitation, rejectPartnerInvitation, authState } = useAuth();
  const t = getTranslations(userLanguage || 'en');
  const queryClient = useQueryClient();
  const sheetRef = useRef<HTMLDivElement>(null);

  // Determina chi ha invitato: user.partnerLinkedBy (utente registrato) o pendingPartnerInvitation (non registrato)
  const invitedBy = (user as any)?.partnerLinkedBy || authState.pendingPartnerInvitation?.invitedBy;
  const isPendingInvitation = !!authState.pendingPartnerInvitation;

  const [animationPhase, setAnimationPhase] = useState<'initial' | 'expanding' | 'complete' | 'closing'>('initial');
  const [isSaving, setIsSaving] = useState(false);

  // Animation effect
  useEffect(() => {
    if (isOpen) {
      setAnimationPhase('initial');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimationPhase('expanding');
          setTimeout(() => setAnimationPhase('complete'), 500);
        });
      });
    } else {
      setAnimationPhase('closing');
    }
  }, [isOpen]);

  const handleClose = () => {
    setAnimationPhase('closing');
    setTimeout(() => {
      onClose();
      setAnimationPhase('initial');
    }, 400);
  };

  const handleForceLinkPartner = async () => {
    try {
      setIsSaving(true);

      if (isPendingInvitation) {
        // CASO 1: Utente NON registrato con pending invitation
        console.log('[MODAL] Confermando pending invitation...');
        const result = await confirmPartnerInvitation();

        if (result.success) {
          handleClose();
        } else {
          alert('Errore: ' + (result.message || 'Conferma fallita'));
        }
      } else {
        // CASO 2: Utente già registrato con partnerLinkedBy
        console.log('[MODAL] Collegamento partner per utente autenticato...');

        // Prima ottieni il telegram ID corrente
        const infoResponse = await fetch('/api/users/me/telegram-info', {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        });

        if (!infoResponse.ok) {
          throw new Error('Impossibile ottenere le informazioni Telegram');
        }

        const data = await infoResponse.json();
        const currentTelegramId = data.telegramId;

        if (!currentTelegramId) {
          alert('Nessun Telegram ID trovato per l\'utente corrente');
          return;
        }

        // Forza il collegamento
        const linkResponse = await fetch('/api/users/force-link-partner', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            partnerTelegramId: currentTelegramId
          })
        });

        if (linkResponse.ok) {
          alert('✅ Partner confermato con successo!');

          // Aggiorna le query
          queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });
          queryClient.invalidateQueries({ queryKey: ["/api/users"] });

          handleClose();
        } else {
          const error = await linkResponse.json();
          throw new Error(error.message || 'Errore nel collegamento');
        }
      }
    } catch (error: any) {
      console.error('[MODAL] Error:', error);
      alert('Errore: ' + (error.message || 'Errore sconosciuto'));
    } finally {
      setIsSaving(false);
    }
  };

    const handleRefuse = async () => {
        try {
            setIsSaving(true);

            console.log('[MODAL] Utente ha rifiutato invito partner...');

            if (isPendingInvitation) {
                // CASO 1: Pending invitation - usa rejectPartnerInvitation
                const result = await rejectPartnerInvitation();

                if (result.success) {
                    console.log('[MODAL] Rifiuto riuscito, ora guest');
                    handleClose();
                } else {
                    console.error('[MODAL] Rifiuto fallito:', result.message);
                    alert('Errore durante il rifiuto. Riprova.');
                }
            } else {
                // CASO 2: Utente autenticato - converti a guest
                console.log('[MODAL] Utente autenticato rifiuta, convertendo a guest...');

                const result = await guestLogin();

                if (result.success) {
                    console.log('[MODAL] Conversione a guest riuscita');

                    // Invalida le query per aggiornare lo stato
                    queryClient.invalidateQueries({ queryKey: ['/api/auth/me'] });

                    handleClose();
                } else {
                    console.error('[MODAL] Conversione a guest fallita:', result.message);
                    alert('Errore durante la conversione a modalità ospite. Riprova.');
                }
            }
        } catch (error: any) {
            console.error('[MODAL] Errore:', error);
            alert('Errore: ' + (error.message || 'Errore sconosciuto'));
        } finally {
            setIsSaving(false);
        }
    }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black transition-all duration-500 ease-out ${ 
          animationPhase === 'initial' ? 'opacity-0' :
          animationPhase === 'closing' ? 'opacity-0' : 'opacity-60'
        }`}
        style={{ zIndex: 49 }}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={`absolute bottom-0 left-0 right-0 rounded-t-3xl overflow-hidden transition-all ${ 
          animationPhase === 'initial' ? 'translate-y-full' :
          animationPhase === 'closing' ? 'translate-y-full duration-400 ease-in' :
          'translate-y-0 duration-500 ease-out'
        }`}
        style={{
          backgroundColor: '#1c1c1e',
          maxHeight: '85vh',
          zIndex: 50
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-center p-4 border-b border-white/10">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-blue-500" />
              Conferma Partner
            </h2>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 text-center">

            <p className="text-lg text-white/90">
                L'utente <strong>{invitedBy}</strong> ti ha aggiunto come partner.
            </p>
            <p className="text-md text-white/70">
                Confermi di essere il suo partner?
            </p>

            <div className="flex gap-4 pt-4">
                <Button
                    onClick={handleRefuse}
                    disabled={isSaving}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
                    >
                    {isSaving ? 'Attendi...' : 'Rifiuta'}
                </Button>
                <Button
                    onClick={handleForceLinkPartner}
                    disabled={isSaving}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold"
                >
                    {isSaving ? 'Confermando...' : 'Conferma'}
                </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
